import { showDialog } from '@/utils/dialogs';
import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  TextInput,
  ActivityIndicator,
  ScrollView,
  Platform,
} from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { MaterialIcons } from '@expo/vector-icons';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useLocalSearchParams } from 'expo-router';

// React Native Reusables Components
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Text } from '@/components/ui/text';

// Services & Components
import { apiRequest } from '../../services/apiClient';
import AppHeaderBar from '../../components/layout/AppHeaderBar';
import { useTranslation } from '../../hooks/useTranslation';
import { acceptWarningText } from '../../components/school/inviteWarnings';
import { brand } from '../../design-system/theme/tokens';
import { trackAuthEvent } from '../../services/analytics';
import { analyticsErrorCode } from '../../services/analyticsPrivacy';

// Professional Design Tokens
const COLORS = {
  primary: brand.primary,
  secondary: '#F59E0B',
  success: '#10B981',
  error: '#EF4444',
  background: '#FFFFFF',
  surface: '#F9FAFB',
  text: {
    primary: '#111827',
    secondary: '#4B5563',
    muted: '#9CA3AF',
  },
  border: '#E5E7EB',
};

interface AcceptInvitationScreenProps {
  onAcceptSuccess: () => void;
}

/**
 * Enhanced Accept Invitation Screen - Professional Design System Implementation
 *
 * - Uses React Native Reusables for consistent professional UI
 * - Complete i18n support with no hardcoded text
 * - Proper safe area handling
 * - Professional design following style guide
 * - B2B school invitation acceptance flow
 * - Clean, accessible interface
 */
export default function AcceptInvitationScreenEnhanced({ onAcceptSuccess }: AcceptInvitationScreenProps) {
  const { t, changeLanguage } = useTranslation();
  const insets = useSafeAreaInsets();
  const params = useLocalSearchParams<{ token?: string | string[]; lang?: string | string[] }>();

  const getFirst = (value?: string | string[]) => (Array.isArray(value) ? value[0] : value);

  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [invitationData, setInvitationData] = useState<{
    email: string;
    full_name?: string;
    school_name: string;
    inviter_name: string;
  } | null>(null);

  const validateToken = useCallback(async (tokenValue: string) => {
    try {
      const res = await apiRequest<any>({
        endpoint: `/auth/validate-invitation?token=${encodeURIComponent(tokenValue)}`,
        method: 'GET',
      });
      trackAuthEvent('invitation_link_opened', { valid: !!res?.valid, error_code: res?.valid ? undefined : 'invitation_invalid' });
      if (res?.valid) {
        setInvitationData({
          email: res.email,
          full_name: res.full_name,
          school_name: res.school_name,
          inviter_name: res.inviter_name,
        });
        if (res.full_name) {
          setFullName((current) => (current.trim() ? current : res.full_name));
        }
      } else {
        setError(t('auth.invitation.invalidLink'));
      }
    } catch (e) {
      trackAuthEvent('invitation_link_opened', { valid: false, error_code: analyticsErrorCode(e) });
      console.log('Validate invitation error', e);
      setError(t('auth.invitation.validationError'));
    }
  }, [t]);

  useEffect(() => {
    // Extract token and language from URL parameters
    const tokenParam = getFirst(params.token);
    const langParam = getFirst(params.lang);

    // Set language if provided in URL
    if (langParam && (langParam === 'tr' || langParam === 'en')) {
      changeLanguage(langParam as 'tr' | 'en');
    }

    if (tokenParam) {
      setToken(tokenParam);
      validateToken(tokenParam);
      return;
    }

    if (Platform.OS === 'web') {
      const url = new URL(window.location.href);
      const fallbackToken = url.searchParams.get('token');
      const fallbackLang = url.searchParams.get('lang');

      // Set language from URL
      if (fallbackLang && (fallbackLang === 'tr' || fallbackLang === 'en')) {
        changeLanguage(fallbackLang as 'tr' | 'en');
      }

      if (fallbackToken) {
        setToken(fallbackToken);
        validateToken(fallbackToken);
      }
    }
  }, [params.token, params.lang, changeLanguage, validateToken]);

  const validateForm = () => {
    setError(null);

    if (!token.trim()) {
      setError(t('auth.invitation.invalidLink'));
      return false;
    }

    if (!fullName.trim()) {
      setError(t('auth.invitation.fullNameRequired'));
      return false;
    }

    return true;
  };

  const handleAcceptInvitation = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      trackAuthEvent('invitation_accept_submitted');

      const response = await apiRequest<any>({
        endpoint: '/auth/accept-invitation',
        method: 'POST',
        data: {
          token: token.trim(),
          full_name: fullName.trim(),
        },
      });

      if (response) {
        // D20: an existing personal account keeps its subscription when it joins.
        const warning = acceptWarningText(t, response?.warning);
        const success = t('auth.invitation.success', { name: fullName });
        showDialog(success, warning || undefined);
        trackAuthEvent('invitation_accept_succeeded', {
          existing_account: !!response?.existing_account,
          has_warning: !!response?.warning,
        });
        onAcceptSuccess();
      } else {
        trackAuthEvent('invitation_accept_failed', { error_code: 'empty_response' });
        setError(t('auth.invitation.acceptFailed'));
      }
    } catch (error) {
      trackAuthEvent('invitation_accept_failed', { error_code: analyticsErrorCode(error) });
      console.error('Error accepting invitation:', error);
      setError(t('auth.invitation.acceptError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <SafeAreaView className="flex-1 bg-surface">
      <AppHeaderBar />
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView
          contentContainerStyle={{
            flexGrow: 1,
            justifyContent: 'center',
            paddingTop: 40,
            paddingBottom: Math.max(insets.bottom + 40, 60),
          }}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag"
          showsVerticalScrollIndicator={false}
        >
          {/* Max-width container for better UX on large screens */}
          <View className="w-full max-w-2xl mx-auto px-4 md:px-6 lg:px-8">
          {/* Header */}
          <View className="items-center mb-12">
            <View
              className="w-20 h-20 rounded-3xl items-center justify-center mb-6"
              style={{ backgroundColor: COLORS.primary }}
            >
              <MaterialIcons name="school" size={40} color="white" />
            </View>

            <Text className="text-3xl font-bold text-center mb-2" style={{ color: COLORS.text.primary }}>
              {t('auth.invitation.title')}
            </Text>
            <Text className="text-base text-center mb-4" style={{ color: COLORS.text.secondary }}>
              {t('auth.invitation.subtitle')}
            </Text>

            {/* Invitation Details */}
            {invitationData && (
              <View
                className="p-4 rounded-lg w-full"
                style={{ backgroundColor: `${COLORS.primary}10`, borderLeftWidth: 3, borderLeftColor: COLORS.primary }}
              >
                {invitationData.school_name && (
                  <Text className="text-sm mb-1" style={{ color: COLORS.text.primary }}>
                    <Text className="font-semibold">{t('auth.invitation.school')}:</Text> {invitationData.school_name}
                  </Text>
                )}
                {invitationData.email && (
                  <Text className="text-sm mb-1" style={{ color: COLORS.text.primary }}>
                    <Text className="font-semibold">{t('auth.invitation.email')}:</Text> {invitationData.email}
                  </Text>
                )}
                {invitationData.inviter_name && (
                  <Text className="text-sm" style={{ color: COLORS.text.primary }}>
                    <Text className="font-semibold">{t('auth.invitation.inviter')}:</Text> {invitationData.inviter_name}
                  </Text>
                )}
              </View>
            )}
          </View>

          {/* Form Card */}
          <Card className="mb-6">
            <CardContent className="p-6">
              {/* Error Message */}
              {error && (
                <View
                  className="flex-row items-center p-4 rounded-lg mb-6"
                  style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', borderColor: COLORS.error, borderWidth: 1 }}
                >
                  <MaterialIcons name="error-outline" size={20} color={COLORS.error} />
                  <Text className="ml-2 flex-1 text-sm" style={{ color: COLORS.error }}>
                    {error}
                  </Text>
                </View>
              )}

              {/* Full Name Input */}
              <View className="mb-4">
                <Text className="text-sm font-medium mb-2" style={{ color: COLORS.text.primary }}>
                  {t('auth.invitation.fullName')}
                </Text>
                <View
                  className="flex-row items-center px-4 rounded-lg"
                  style={{
                    backgroundColor: COLORS.background,
                    borderWidth: 1,
                    borderColor: COLORS.border,
                    height: 48,
                  }}
                >
                  <MaterialIcons name="person" size={20} color={COLORS.text.muted} />
                  <TextInput
                    className="flex-1 ml-3 text-base"
                    style={{ color: COLORS.text.primary }}
                    placeholder={t('auth.invitation.fullNamePlaceholder')}
                    placeholderTextColor={COLORS.text.muted}
                    value={fullName}
                    onChangeText={(text) => {
                      setFullName(text);
                      if (error) setError(null);
                    }}
                    autoCapitalize="words"
                    autoCorrect={false}
                    editable={!loading}
                  />
                </View>
              </View>

              {/* Passwordless Notice */}
              <View
                className="p-4 rounded-lg mb-6"
                style={{ backgroundColor: `${COLORS.primary}10` }}
              >
                <Text className="text-sm font-semibold mb-2" style={{ color: COLORS.text.primary }}>
                  {t('auth.invitation.passwordlessTitle')}
                </Text>
                <Text className="text-sm" style={{ color: COLORS.text.secondary }}>
                  {t('auth.invitation.passwordlessBody')}
                </Text>
              </View>

              {/* Create Account Button */}
              <Button
                variant="default"
                size="default"
                onPress={handleAcceptInvitation}
                disabled={loading}
                className="mb-4"
              >
                {loading ? (
                  <View className="flex-row items-center">
                    <ActivityIndicator size="small" color="white" className="mr-2" />
                    <Text className="text-white font-semibold">
                      {t('common.loading')}
                    </Text>
                  </View>
                ) : (
                  <View className="flex-row items-center">
                    <MaterialIcons name="check-circle" size={20} color="white" className="mr-2" />
                    <Text className="text-white font-semibold">
                      {t('auth.invitation.createButton')}
                    </Text>
                  </View>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Terms */}
          <View className="items-center mt-4">
            <Text className="text-xs text-center leading-5" style={{ color: COLORS.text.muted }}>
              {t('auth.invitation.terms')}
            </Text>
          </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
